import { useEffect, useRef, useState } from 'react';
import { useDiagramData } from '@/hooks/useDiagramData';
import { useApp } from '@/contexts/AppContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useWriteBackLock } from '@/hooks/useWriteBackLock';
import { useProject } from '@/contexts/ProjectContext';
import { READ_ONLY_WRITE_BACK_MESSAGE, STANDALONE_WRITE_BACK_MESSAGE } from '@/lib/projectVersionState';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pencil } from 'lucide-react';
import ReviewCommentsModal, { ReviewCommentsIndicator } from '@/components/review/ReviewCommentsModal';
import ConnectModal from '@/components/threatcat/ConnectModal';
import EditBoundaryDialog, { type BoundaryEdit } from '@/components/tabs/boundaries/EditBoundaryDialog';
import { threatcatService } from '@/services/threatcatService';
import { draftDiagramService, type DraftDiagramOut } from '@/services/draftDiagramService';
import { ThreatCatAuthError } from '@/lib/errors';
import type { Boundary } from '@/types/diagram';

interface BoundariesTabProps {
  draftId?: number;
  onDraftUpdated?: (draft: DraftDiagramOut) => void;
}

export default function BoundariesTab({ draftId, onDraftUpdated }: BoundariesTabProps = {}) {
  const isDraft = draftId != null;
  const { currentDiagram } = useDiagramData();
  const { diagrams, setAppState } = useApp();
  const { cookie, getCookie, disconnect } = useThreatCat();
  const { isLocked: isWriteBackLocked, message: writeBackLockMessage } = useWriteBackLock();
  const reviewIndex = useReviewCommentsIndex();
  const { projectId, detail } = useProject();
  const isStandaloneDiagram = !isDraft && projectId == null;
  const isReadOnly = detail?.permission === 'read_only';
  const isBoundaryWriteLocked = !isDraft && isWriteBackLocked;
  const isEditDisabled = isStandaloneDiagram || isReadOnly || isBoundaryWriteLocked;
  const editDisabledReason = isStandaloneDiagram
    ? STANDALONE_WRITE_BACK_MESSAGE
    : isReadOnly
      ? READ_ONLY_WRITE_BACK_MESSAGE
      : isBoundaryWriteLocked
        ? writeBackLockMessage
        : null;
  const [reviewModalBoundaryId, setReviewModalBoundaryId] = useState<number | null>(null);

  // ── Edit boundary state ──────────────────────────────────────────────────────
  const [editingBoundary, setEditingBoundary] = useState<Boundary | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const pendingSaveRef = useRef<Boundary | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => clearCloseTimer, []);

  if (!currentDiagram) return null;

  const activeReviewBoundary =
    reviewModalBoundaryId !== null
      ? currentDiagram.boundaries.find((b) => b.id === reviewModalBoundaryId) ?? null
      : null;
  const activeReviewBoundaryComments = activeReviewBoundary
    ? reviewIndex.getBoundaryComments(activeReviewBoundary.id, currentDiagram.id)
    : [];

  // Build boundary hierarchy
  const rootBoundaries = currentDiagram.boundaries.filter((b) => !b.parent_boundary_id);
  const boundaryChildren: Record<number, typeof currentDiagram.boundaries> = {};

  currentDiagram.boundaries.forEach((boundary) => {
    if (boundary.parent_boundary_id) {
      if (!boundaryChildren[boundary.parent_boundary_id]) {
        boundaryChildren[boundary.parent_boundary_id] = [];
      }
      boundaryChildren[boundary.parent_boundary_id].push(boundary);
    }
  });

  // Count entities per boundary
  const entityCounts: Record<number, number> = {};
  currentDiagram.entities.forEach((entity) => {
    entityCounts[entity.boundary_id] = (entityCounts[entity.boundary_id] || 0) + 1;
  });

  const openEditDialog = (boundary: Boundary) => {
    if (isEditDisabled) return;
    clearCloseTimer();
    setEditingBoundary(boundary);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const executeSave = async (updated: Boundary, activeCookie?: string) => {
    if (isEditDisabled) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updatedBoundaries = currentDiagram.boundaries.map((b) => (b.id === updated.id ? updated : b));
      if (isDraft) {
        const updatedDraft = await draftDiagramService.patchPayload(draftId, { boundaries: updatedBoundaries });
        setAppState({ currentDiagram: { ...currentDiagram, boundaries: updatedBoundaries } });
        onDraftUpdated?.(updatedDraft);
      } else {
        const useCookie = activeCookie ?? cookie;
        if (!useCookie) return;
        await threatcatService.updateBoundary({
          cookie: useCookie,
          id: updated.id,
          diagram_id: currentDiagram.id,
          name: updated.name,
          description: updated.description ?? '',
          parent_boundary_id: updated.parent_boundary_id ?? null,
        });
        setAppState({
          diagrams: diagrams.map((d) =>
            d.id === currentDiagram.id
              ? { ...d, boundaries: d.boundaries.map((b) => (b.id === updated.id ? updated : b)) }
              : d,
          ),
        });
      }

      setSaveSuccess(true);
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        setEditingBoundary(null);
        setSaveSuccess(false);
      }, 1200);
    } catch (err) {
      if (!isDraft && err instanceof ThreatCatAuthError) {
        // Expired cookie: reconnect and resume this save once connected.
        disconnect();
        pendingSaveRef.current = updated;
        setConnectOpen(true);
        return;
      }
      setSaveError(err instanceof Error ? err.message : 'Failed to update boundary');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (edit: BoundaryEdit) => {
    if (!editingBoundary || isEditDisabled) return;
    // Spread keeps position/style (and any other metadata) untouched.
    const updated: Boundary = { ...editingBoundary, ...edit };

    if (!isDraft && !cookie) {
      pendingSaveRef.current = updated;
      setConnectOpen(true);
      return;
    }
    await executeSave(updated);
  };

  const handleConnected = () => {
    setConnectOpen(false);
    const nextCookie = getCookie();
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    if (isEditDisabled) return;
    if (pending && nextCookie) {
      void executeSave(pending, nextCookie);
    }
  };

  const handleConnectClose = () => {
    setConnectOpen(false);
    pendingSaveRef.current = null;
  };

  const editTitle = isDraft ? 'Edit boundary' : 'Edit and sync to ThreatCat';

  const renderBoundary = (boundary: Boundary, level: number = 0) => {
    const children = boundaryChildren[boundary.id] || [];
    const entityCount = entityCounts[boundary.id] || 0;
    const boundaryComments = reviewIndex.getBoundaryComments(boundary.id, currentDiagram.id);

    return (
      <div key={boundary.id} className="space-y-2" style={{ marginLeft: `${level * 24}px` }}>
        <div className="rounded-lg border bg-card p-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="font-medium">{boundary.name}</div>
              {boundary.description && (
                <div className="mt-1 text-sm text-muted-foreground">{boundary.description}</div>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <ReviewCommentsIndicator
                count={boundaryComments.length}
                onClick={() => setReviewModalBoundaryId(boundary.id)}
                triggerProps={{ 'data-boundary-review-trigger': boundary.id }}
              />
              {entityCount > 0 && (
                <span className="rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                  {entityCount} {entityCount === 1 ? 'entity' : 'entities'}
                </span>
              )}
              {children.length > 0 && (
                <span className="rounded-md bg-muted px-2 py-1 text-xs font-medium text-foreground ring-1 ring-inset ring-border">
                  {children.length} {children.length === 1 ? 'child' : 'children'}
                </span>
              )}
              <span title={editDisabledReason ?? undefined} className="inline-flex">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  aria-label="Edit boundary"
                  data-boundary-edit-trigger={boundary.id}
                  title={editDisabledReason ?? editTitle}
                  disabled={isEditDisabled}
                  onClick={() => openEditDialog(boundary)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </span>
            </div>
          </div>
        </div>
        {children.length > 0 && (
          <div className="space-y-2">
            {children.map((child) => renderBoundary(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ConnectModal open={connectOpen} onClose={handleConnectClose} onConnected={handleConnected} />
      {editingBoundary && (
        <EditBoundaryDialog
          key={editingBoundary.id}
          boundary={editingBoundary}
          boundaries={currentDiagram.boundaries}
          isDraft={isDraft}
          isSaving={isSaving}
          saveError={saveError}
          saveSuccess={saveSuccess}
          onCancel={() => {
            clearCloseTimer();
            pendingSaveRef.current = null;
            setEditingBoundary(null);
          }}
          onSave={(edit) => void handleSave(edit)}
        />
      )}
      <ReviewCommentsModal
        open={activeReviewBoundary !== null && activeReviewBoundaryComments.length > 0}
        onClose={() => setReviewModalBoundaryId(null)}
        targetType="Boundary"
        targetLabel={activeReviewBoundary?.name || ''}
        targetSubtitle={activeReviewBoundary?.description || undefined}
        comments={activeReviewBoundaryComments}
      />
      <Card>
        <CardHeader>
          <CardTitle>System Boundaries</CardTitle>
          <CardDescription>Hierarchical view of trust boundaries</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {rootBoundaries.map((boundary) => renderBoundary(boundary, 0))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
